/* eslint-disable arrow-parens */
import { useCallback, useEffect, useContext, useState, useMemo, Suspense } from "react";
import { Paper, Grid, Checkbox } from "@mui/material";
import PlanHeader from "../../../../../shared/enrollment/planCode/PlanHeader";
import GSTD_CONTENT from "../../../../../../constants/planContents/GSTD.json";
import LinkIcon from "@mui/icons-material/Link";
import CircleIcon from "@mui/icons-material/Circle";
import {
	AuthContext,
	GroupEnrollmentContext,
	GroupOwnerContext,
	PaycheckContext,
	ThemeContext
} from "../../../../../../contexts";
import EventNoteIcon from "@mui/icons-material/EventNote";
import { CustomInput } from "../../../../../shared/CustomInput";
import { useHistory, useLocation } from "react-router-dom";

import "./gstdForm.css";
import PlanActions from "../../../../../shared/enrollment/planActions/PlanActions";
import { PlanFormProps } from "../../../../../../types/components/enrollmentProps.types";
import { CoverageDetails } from "../../../../../shared/enrollment";
import { numberToDollarStr } from "../../../../../../utils/commonFunctions/dollar";
import { PremiumRateCoverage } from "../../../../../../types/plan.types";
import {
	dateTimeZoneConverter,
	getCurrentDate,
	manualDateComparator
} from "../../../../../../utils/commonFunctions/date";
import { CommonDetails } from "../../../../../../types/enrollment.types";
import { api } from "../../../../../../utils/api";
import { AddEnrollmentPayload } from "../../../../../../utils/api/enrollment/enrollment";
import { CustomFormDialogType } from "../../../../../../types/components/dialogProps.types";
import { generateGSTDWaiveEnrollmentPayload } from "../../../../../../utils/commonFunctions/groupEnrollment/enrollment";
import { LazyCustomFormDialog } from "../../../../../shared";
import { trackPromise } from "react-promise-tracker";
import { GLTDClass1PlanBrochure, GLTDClass2PlanBrochure } from "../../../../../../assets";

const GstdForm = ({
	dependents,
	isActiveEnrollment,
	getEnrollmentInitData,
	openEnrollment
}: PlanFormProps): JSX.Element => {
	const { member } = useContext(AuthContext);
	const [premiumAmount, setPremiumAmount] = useState<number | null>(null);
	const [checkboxOption, setCheckboxOption] = useState("");

	const { paycheck } = useContext(PaycheckContext);
	const [coverageDetails, setCoverageDetails] = useState<any[]>([]);
	const { theme } = useContext(ThemeContext);
	const { deleteCurrentEnrollment } = useContext(GroupEnrollmentContext);
	const history = useHistory();
	const location = useLocation();
	const [waiveReason, setWaiveReason] = useState("");
	const [isWaiveEnrollmentTriggered, setIsWaiveEnrollmentTriggered] = useState(false);
	const urlSearchParams: URLSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const { groupOwner } = useContext(GroupOwnerContext);

	const handleWaiveReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setWaiveReason(value);
	};

	const [customFormDialogProps, setCustomFormDialogProps] = useState<CustomFormDialogType>();

	const handleWaiveEnrollment = useCallback(async () => {
		if (member === null || openEnrollment === null) {
			return;
		}
		const { standardDetails, commonDetails } = generateGSTDWaiveEnrollmentPayload(
			member,
			openEnrollment,
			waiveReason
		);

		if (isActiveEnrollment) {
			// console.log("isActiveEnrollment", isActiveEnrollment);
			commonDetails.enrollment_status = "WAIVED - WAITING FOR APPROVAL";
			// debugger;
		}
		const enrollmentPayload: AddEnrollmentPayload = {
			standard_details: standardDetails,
			common_details: { ...commonDetails, agent_id: groupOwner && groupOwner._id ? groupOwner._id : null }
		};
		const response = await api.enrollment.addEnrollment(enrollmentPayload);
		if (response) {
			getEnrollmentInitData(false);
		}
		const step = urlSearchParams.get("step");
		history.push(`?step=${step}&stage=2`);
	}, [
		getEnrollmentInitData,
		groupOwner,
		history,
		isActiveEnrollment,
		member,
		openEnrollment,
		urlSearchParams,
		waiveReason
	]);

	const handleWaiveButtonClick = useCallback(() => {
		setCustomFormDialogProps(
			Object.assign({}, customFormDialogProps, {
				openDialog: true,
				title: "Are you sure want to waive this enrollment?",
				textfields: [
					{
						label: "Waive Reason",
						onChange: handleWaiveReasonChange,
						value: waiveReason,
						placeholder: "Enter your reason for waive"
					}
				],
				actions: [
					{
						label: "Cancel",
						callback: () =>
							setCustomFormDialogProps(Object.assign({}, customFormDialogProps, { openDialog: false }))
					},
					{
						label: "Yes",
						callback: () => {
							setCustomFormDialogProps(
								Object.assign({}, customFormDialogProps, {
									openDialog: false
								})
							);
							// handleWaiveEnrollment();
							setIsWaiveEnrollmentTriggered(true);
						}
					}
				]
			})
		);
	}, [customFormDialogProps, waiveReason]);

	const getPremiumAmount = useCallback(
		(premiumRateCoverage: PremiumRateCoverage): string => {
			let premiumAmount = "";
			if (paycheck) {
				if (premiumRateCoverage.premium_type === "FIXED") {
					premiumAmount =
						(paycheck.pay_frequency === "WEEKLY"
							? premiumRateCoverage.weekly_premium
							: premiumRateCoverage.monthly_premium) + "";
					premiumAmount = `${numberToDollarStr(Number(premiumAmount))}`;
				} else if (premiumRateCoverage.premium_type === "PERCENTAGE") {
					premiumAmount =
						(paycheck.pay_frequency === "WEEKLY"
							? premiumRateCoverage.weekly_premium
							: premiumRateCoverage.monthly_premium) + "";
					premiumAmount = premiumAmount + "%";
				}
			}
			return premiumAmount;
		},
		[paycheck]
	);

	const getPremiumRateByFixed = useCallback(
		(basePayRate: number, coverageCode: PremiumRateCoverage): number | null => {
			if (paycheck === null) {
				return null;
			}
			let premiumAmount: number | null = null;
			if (paycheck.pay_frequency === "MONTHLY") {
				premiumAmount = coverageCode.monthly_premium as number;
			} else if (paycheck.pay_frequency === "WEEKLY") {
				premiumAmount = coverageCode.weekly_premium as number;
			}
			return premiumAmount;
		},
		[paycheck]
	);

	const getPremiumRateByPercentage = useCallback(
		(basePayRate: number, coverageCode: PremiumRateCoverage): number | null => {
			if (paycheck === null) {
				return null;
			}
			let premiumAmount: number | null = null;
			if (paycheck.pay_frequency === "MONTHLY") {
				premiumAmount = (basePayRate * ((coverageCode.monthly_premium as number) / 100)) as number;
				if (premiumAmount > (coverageCode.monthly_maximum_premium as number)) {
					premiumAmount = coverageCode.monthly_maximum_premium as number;
				} else if (premiumAmount < (coverageCode.monthly_minimum_premium as number)) {
					premiumAmount = coverageCode.monthly_minimum_premium as number;
				}
			} else if (paycheck.pay_frequency === "WEEKLY") {
				premiumAmount = (basePayRate * ((coverageCode.weekly_premium as number) / 100)) as number;
				if (premiumAmount > (coverageCode.weekly_maximum_premium as number)) {
					premiumAmount = coverageCode.weekly_maximum_premium as number;
				} else if (premiumAmount < (coverageCode.weekly_minimum_premium as number)) {
					premiumAmount = coverageCode.weekly_minimum_premium as number;
				}
			}
			return premiumAmount;
		},
		[paycheck]
	);

	const calculatePremiumByCoverage = useCallback(
		(basePayRate: number, coverageCode: PremiumRateCoverage): number | null => {
			if (openEnrollment === null || paycheck === null) {
				return null;
			}

			let premiumAmount: number | null;
			if (coverageCode.active === false) {
				alert("Member is not covered in this plan");
				return null;
			}
			if (coverageCode.premium_type === "PERCENTAGE") {
				premiumAmount = getPremiumRateByPercentage(basePayRate, coverageCode);
			} else if (coverageCode.premium_type === "FIXED") {
				premiumAmount = getPremiumRateByFixed(basePayRate, coverageCode);
			} else {
				alert("Invalid premium type");
				return null;
			}
			return premiumAmount;
		},
		[getPremiumRateByFixed, getPremiumRateByPercentage, openEnrollment, paycheck]
	);

	const getCoverageDetails = useCallback(() => {
		const _coverageDetails: any[] = [];
		if (openEnrollment === null || paycheck === null) {
			return;
		}
		const {
			plan: { coverage }
		} = openEnrollment;
		if (coverage.EE.active === true) {
			const premiumAmount = getPremiumAmount(coverage.EE);

			_coverageDetails.push({
				coverage: "Employee Only",
				premiumAmount
			});
		}
		setCoverageDetails(Object.assign([], _coverageDetails));
	}, [getPremiumAmount, openEnrollment, paycheck]);

	const calculatePremium = useCallback(() => {
		if (openEnrollment === null || paycheck === null) {
			return null;
		}

		let basePayRate: number;
		if (paycheck?.pay_frequency === "WEEKLY") {
			basePayRate = paycheck.weekly_base_rate;
		} else if (paycheck?.pay_frequency === "MONTHLY") {
			basePayRate = paycheck.monthly_base_rate;
		} else {
			alert("Invalid Pay Frequency");
			return;
		}
		let employeePremium: number | null = 0;
		employeePremium = calculatePremiumByCoverage(basePayRate, openEnrollment.plan.coverage.EE);
		setPremiumAmount(Number(employeePremium?.toFixed(2)));
		if (employeePremium === null) {
			return;
		}
		if (openEnrollment) {
			// setCurrentEnrollment(
			// 	Object.assign(
			// 		{},
			// 		{
			// 			plan_name: openEnrollment.plan.plan_name,
			// 			status: "Current",
			// 			premium_amount: employeePremium
			// 		}
			// 	)
			// );
		}
	}, [calculatePremiumByCoverage, openEnrollment, paycheck]);

	const handleCheckboxValueChange = useCallback((event) => {
		const { name, checked } = event.target;
		setCheckboxOption(name);
	}, []);

	const handleActivateEnrollment = useCallback(async () => {
		if (member === null || openEnrollment === null || paycheck === null || premiumAmount === null) {
			return;
		}

		const currentDate = new Date() as unknown as string;
		const planStartDate = openEnrollment.plan.start_date; // -1 or 0
		const planDateComparison: number = manualDateComparator(planStartDate, currentDate);

		const effectiveDate = openEnrollment.plan.auto_approve
			? planDateComparison === -1 || planDateComparison === 0
				? getCurrentDate()
				: null
			: null;
		const enrollmentCommonDetails: CommonDetails = {
			agent_id: groupOwner && groupOwner._id ? groupOwner._id : null,
			location_number: member.location.location_number,
			location_name: member.location.location_name,
			group_number: member.group_number,
			group_name: member.group.name,
			plan_object_id: openEnrollment.plan._id,
			plan_code: openEnrollment.plan.plan_code,
			plan_name: openEnrollment.plan.plan_name,
			base_plan_name: openEnrollment.plan.plan_name,
			insured_object_id: member._id,
			insured_SSN: member.SSN,
			writing_number: 0,
			unenrolled_reason: null,
			waive_reason: null,
			termination_reason: null,
			enrollment_date: getCurrentDate(),
			effective_date: effectiveDate,
			termination_date: null,
			open_enrollment_id: openEnrollment._id,
			total_premium: premiumAmount
		};
		const enrollmentPayload: AddEnrollmentPayload = {
			common_details: enrollmentCommonDetails,
			standard_details: [
				{
					member_object_id: member._id,
					member_SSN: member.SSN,
					coverage_code: "EE",
					premium_amount: premiumAmount,
					is_tobacco: false
				}
			]
		};
		const response = await trackPromise(api.enrollment.addEnrollment(enrollmentPayload));
		if (response) {
			const step = urlSearchParams.get("step");
			getEnrollmentInitData(false);
			history.push(`?step=${step}&stage=0`);
		}
	}, [getEnrollmentInitData, groupOwner, history, member, openEnrollment, paycheck, premiumAmount, urlSearchParams]);

	useEffect(() => {
		getCoverageDetails();
		calculatePremium();
	}, [calculatePremium, getCoverageDetails]);

	useEffect(() => {
		if (isWaiveEnrollmentTriggered) {
			handleWaiveEnrollment();
			setIsWaiveEnrollmentTriggered(false); // Reset the trigger
		}
	}, [isWaiveEnrollmentTriggered, handleWaiveEnrollment]);

	if (!member) return <></>;

	return (
		<div className="GSTD theme-container">
			<Suspense fallback={<div />}>
				{customFormDialogProps ? <LazyCustomFormDialog customFormDialogProps={customFormDialogProps} /> : null}
			</Suspense>
			<Paper className="paper-container">
				<PlanHeader planCode={openEnrollment?.plan.plan_code} planName="Group Short Term Disability" />
				<div
					className="theme-divider-grad-mask"
					style={{
						background: `linear-gradient(90deg, ${theme.primary_color} 0 55%, transparent 0 100%) 0 0 / 2rem 1rem`
					}}
				/>
				<div className="theme-plan-section-margin" />
				{/* <div className="option-content">
					<Checkbox
						name="yes"
						onChange={handleCheckboxValueChange}
						checked={checkboxOption === "yes" ? true : false}
					/>
					<p className="checkbox-label">
						<span className="first-word">YES</span>
						{GSTD_CONTENT["checkbox-1-text"]}
					</p>
				</div>
				<div className="option-content">
					<Checkbox
						name="no"
						onChange={handleCheckboxValueChange}
						checked={checkboxOption === "no" ? true : false}
					/>
					<p className="checkbox-label">
						<span className="first-word">NO</span>
						{GSTD_CONTENT["checkbox-2-text"]}
					</p>
				</div>
				<div className="option-content">
					<Checkbox
						name="terminate"
						onChange={handleCheckboxValueChange}
						checked={checkboxOption === "terminate" ? true : false}
					/>
					<p className="checkbox-label">
						<span className="first-word underline">TERMINATE COVERAGE</span>
						{GSTD_CONTENT["checkbox-2-text"]}
					</p>
				</div> */}
				<div className="theme-plan-section-margin" />
				<div className="external-links">
					<div>
						<LinkIcon className="link-icon" style={{ color: theme.primary_color }} />
					</div>
					<div className="header">
						Click on the following links to know more about <strong>Class 1</strong> and{" "}
						<strong>Class 2</strong> benefits
					</div>
				</div>
				<div className="theme-plan-inner-section-margin" />
				<div className="link-container">
					<div className="theme-plan-icon-item-center-container">
						<div>
							<CircleIcon className="theme-plan-subpoint-icon" />
						</div>
						<div className="theme-plan-subpoint-text">
							<a href={GLTDClass1PlanBrochure} download="GLTD_Class_1_Plan_Brochure.pdf">
								Class 1 Benefits
							</a>{" "}
							- Full-Time Employees other than truck drivers
						</div>
					</div>
					<div className="theme-plan-sub-inner-section-margin" />
					<div className="theme-plan-icon-item-center-container">
						<div>
							<CircleIcon className="theme-plan-subpoint-icon" />
						</div>
						<div className="theme-plan-subpoint-text">
							<a href={GLTDClass2PlanBrochure} download="GLTD_Class_2_Plan_Brochure.pdf">
								Class 2 Benefits
							</a>{" "}
							- Full-Time truck drivers
						</div>
					</div>
				</div>
				<div className="theme-plan-section-margin" />
				<CoverageDetails coverageDetails={coverageDetails} />
				<div className="theme-plan-section-margin" />
				<div className="theme-plan-icon-item-container">
					<div>
						<EventNoteIcon className="event-icon" style={{ color: theme.primary_color }} />
					</div>
					<div className="theme-plan-text">
						<strong>Note:</strong> Participants should refer to the summary plan description for the
						Cafeteria Plan for additional status change and election change events
					</div>
				</div>
				<div style={{ marginTop: "4rem" }} />
				<div>
					<div className="details-form-row">
						<div className="details-form-label  required">Premium Amount</div>
						<CustomInput value={`$ ${premiumAmount}`} disabled />
					</div>
				</div>
				<div className="theme-plan-section-margin" />
				<div className="option-content">
					<Checkbox defaultChecked style={{ paddingLeft: 0 }} />
					<p className="checkbox-label">
						<strong>
							I UNDERSTAND AND AGREE TO THE TERMS AND CONDITIONS STATED ABOVE AND AUTHORIZE
							THE&nbsp;PAYROLL DEDUCTIONS TO BE MADE AS INDICATED
						</strong>
					</p>
				</div>
				<div className="theme-plan-section-margin" />
				<Grid container>
					<Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
						<div className="details-form-row">
							<div className="details-form-label  required">Signature</div>
							<CustomInput value={member.first_name + " " + member.last_name} disabled />
						</div>
					</Grid>
					<Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
						<div className="details-form-row">
							<div className="details-form-label  required">Date</div>
							<CustomInput value={dateTimeZoneConverter(getCurrentDate())} disabled />
						</div>
					</Grid>
				</Grid>
				<div className="theme-plan-section-margin" />
				<PlanActions
					waiveButtonCallback={handleWaiveButtonClick}
					activateButtonCallback={handleActivateEnrollment}
					// checkboxOptions={checkboxOption}
				/>
			</Paper>
		</div>
	);
};

export default GstdForm;
